
import { Room } from "@/types/chat";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { Plus, UsersRound } from "lucide-react";

interface RoomsListProps {
  rooms: Room[] | undefined;
  selectedRoom: Room | null;
  onSelectRoom: (room: Room) => void;
  onCreateChat?: () => void;
  className?: string;
}

export const RoomsList = ({ 
  rooms, 
  selectedRoom, 
  onSelectRoom,
  onCreateChat,
  className 
}: RoomsListProps) => {
  const isMobile = useIsMobile();

  if (!rooms?.length) {
    return (
      <div className={`w-full h-[calc(100vh-4rem)] flex flex-col items-center justify-center ${className}`}>
        <div className="text-center space-y-3 max-w-xs px-4">
          <UsersRound className="h-12 w-12 text-muted-foreground mx-auto" />
          <h3 className="font-medium text-lg">No conversations yet</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Start chatting with other users to build your network
          </p>
          <Button 
            onClick={onCreateChat} 
            className="bg-flux-cyan hover:bg-flux-cyan/90 text-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Conversation
          </Button>
        </div>
      </div>
    );
  }

  // Separate group and direct message rooms
  const groupRooms = rooms.filter(room => room.is_group);
  const directRooms = rooms.filter(room => !room.is_group);

  return (
    <div className={`w-full lg:w-80 border-r bg-card h-[calc(100vh-4rem)] ${className} ${
      isMobile && selectedRoom ? 'hidden' : 'block'
    } lg:block`}>
      <div className="p-3 border-b flex justify-between items-center">
        <h2 className="text-lg font-semibold">Messages</h2>
        <Button 
          onClick={onCreateChat} 
          variant="ghost" 
          size="icon" 
          className="rounded-full hover:bg-flux-cyan/10"
        >
          <Plus className="h-5 w-5 text-flux-cyan" />
        </Button>
      </div>
      <ScrollArea className="h-[calc(100vh-8rem)]">
        <div className="space-y-4 p-2">
          {groupRooms.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground px-3 py-2">Startups & Groups</h3>
              <div className="space-y-1">
                {groupRooms.map((room) => (
                  <Button
                    key={room.id}
                    variant={selectedRoom?.id === room.id ? "secondary" : "ghost"}
                    className={`w-full justify-start px-4 py-6 ${
                      selectedRoom?.id === room.id ? "bg-accent" : ""
                    }`}
                    onClick={() => onSelectRoom(room)}
                  >
                    <div className="flex items-center gap-3 w-full">
                      <Avatar>
                        {room.startup?.banner_url ? (
                          <AvatarImage src={room.startup.banner_url} />
                        ) : (
                          <AvatarFallback className="bg-flux-cyan/20">
                            <UsersRound className="h-5 w-5 text-flux-cyan" />
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div className="flex flex-col items-start overflow-hidden">
                        <span className="font-medium truncate w-full">
                          {room.name || 'Group Chat'}
                        </span>
                        <span className="text-xs text-muted-foreground truncate w-full">
                          {room.last_message || "No messages yet"}
                        </span>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {directRooms.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground px-3 py-2">Direct Messages</h3>
              <div className="space-y-1">
                {directRooms.map((room) => (
                  <Button
                    key={room.id}
                    variant={selectedRoom?.id === room.id ? "secondary" : "ghost"}
                    className={`w-full justify-start px-4 py-6 ${
                      selectedRoom?.id === room.id ? "bg-accent" : ""
                    }`}
                    onClick={() => onSelectRoom(room)}
                  >
                    <div className="flex items-center gap-3 w-full">
                      <Avatar>
                        <AvatarImage src={room.participants?.[0]?.profiles?.avatar_url || undefined} />
                        <AvatarFallback>
                          {room.participants?.[0]?.profiles?.username?.[0]?.toUpperCase() || room.name?.[0]?.toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col items-start overflow-hidden">
                        <span className="font-medium truncate w-full">
                          {room.participants?.[0]?.profiles?.username || room.name || 'Unknown User'}
                        </span>
                        <span className="text-xs text-muted-foreground truncate w-full">
                          {room.last_message || "No messages yet"}
                        </span>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
